import { type WorkfrontLookup, WorkfrontMappingKind, WorkfrontValueMatch } from '@atlas/types'
import { type QueryClient, type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	type AssetTypeListRow,
	type FieldMappingListRow,
	type LookupListRow,
	type LookupRow,
	type MappingRow,
	type MappingRowStatus,
	type MappingStatusDTO,
	type UnmappedFieldDTO,
	type ValueListParams,
	type ValueListRow,
	type ValueRow,
	workfrontMappingsApi,
} from '@/services/workfront-mappings.service'
import { WORKFRONT_KEY } from './useWorkfront'

/**
 * React-query hooks for Admin › Workfront › Mappings. Every key sits under
 * `['workfront', 'mappings']`, so one invalidation refreshes every tab, and a
 * publish invalidates the whole Workfront area (errors and bundles re-map).
 */
export const MAPPINGS_KEY = [...WORKFRONT_KEY, 'mappings'] as const
export const mappingKeys = {
	catalogue: [...MAPPINGS_KEY, 'catalogue'] as const,
	status: [...MAPPINGS_KEY, 'status'] as const,
	fields: [...MAPPINGS_KEY, 'fields'] as const,
	assetTypes: [...MAPPINGS_KEY, 'asset-types'] as const,
	lookups: [...MAPPINGS_KEY, 'lookups'] as const,
	values: [...MAPPINGS_KEY, 'values'] as const,
	proposals: (axis: string) => [...MAPPINGS_KEY, 'proposals', axis] as const,
	versions: [...MAPPINGS_KEY, 'versions'] as const,
	unmapped: [...MAPPINGS_KEY, 'unmapped-fields'] as const,
	parameters: (search: string) => [...MAPPINGS_KEY, 'parameters', search] as const,
	// Not under `bundle`: the error patches there expect a bundle detail.
	canonical: (bundleId: string) => [...WORKFRONT_KEY, 'canonical', bundleId] as const,
}

export const useMappingCatalogue = () =>
	useQuery({ queryKey: mappingKeys.catalogue, queryFn: workfrontMappingsApi.catalogue, staleTime: Infinity })
export const useMappingStatus = () => useQuery({ queryKey: mappingKeys.status, queryFn: workfrontMappingsApi.status })
export const useFieldMappings = () => useQuery({ queryKey: mappingKeys.fields, queryFn: workfrontMappingsApi.fields })
export const useAssetTypeMappings = () =>
	useQuery({ queryKey: mappingKeys.assetTypes, queryFn: workfrontMappingsApi.assetTypes })
export const useLookupRows = (lookup: WorkfrontLookup) =>
	useQuery({ queryKey: [...mappingKeys.lookups, lookup], queryFn: () => workfrontMappingsApi.lookups(lookup) })
export const useValueMappings = (params: ValueListParams) =>
	useQuery({ queryKey: [...mappingKeys.values, params], queryFn: () => workfrontMappingsApi.values(params) })
export const useValueProposals = (axis: string | undefined) =>
	useQuery({
		queryKey: mappingKeys.proposals(axis ?? ''),
		queryFn: () => workfrontMappingsApi.proposals(axis ?? ''),
		enabled: !!axis,
	})
export const useMappingVersions = () =>
	useQuery({ queryKey: mappingKeys.versions, queryFn: workfrontMappingsApi.versions })
export const useUnmappedFields = () =>
	useQuery({ queryKey: mappingKeys.unmapped, queryFn: workfrontMappingsApi.unmappedFields })
export const useWorkfrontParameters = (search: string) =>
	useQuery({
		queryKey: mappingKeys.parameters(search),
		queryFn: () => workfrontMappingsApi.parameters(search),
		staleTime: 60_000,
	})
export const useBundleCanonical = (bundleId: string) =>
	useQuery({ queryKey: mappingKeys.canonical(bundleId), queryFn: () => workfrontMappingsApi.canonical(bundleId) })

type Snapshot = [QueryKey, unknown][]

async function snapshotOf(qc: QueryClient): Promise<Snapshot> {
	await qc.cancelQueries({ queryKey: MAPPINGS_KEY })
	return qc.getQueriesData({ queryKey: MAPPINGS_KEY })
}

const restore = (qc: QueryClient, snapshot: Snapshot | undefined) => {
	for (const [key, value] of snapshot ?? []) qc.setQueryData(key, value)
}

const invalidateMappings = (qc: QueryClient) => qc.invalidateQueries({ queryKey: MAPPINGS_KEY })

/** The draft revision the next save is made on: the open draft's, or 0 to open one. */
const revisionOf = (qc: QueryClient) => qc.getQueryData<MappingStatusDTO>(mappingKeys.status)?.draft?.revision ?? 0

/** One row to save into the draft: the key it was listed under (any placeholder for a new one), and what it becomes. */
export interface DraftRowSave {
	key: string
	row: MappingRow
	/** For a value row, the ATLAS value's label, shown at once in its place. */
	valueLabel?: string
}

const edited = (status: MappingRowStatus | 'unmapped'): MappingRowStatus => (status === 'added' ? 'added' : 'changed')

function upsert<T extends { key: string; status: MappingRowStatus }>(rows: T[], save: DraftRowSave, created: () => T) {
	const found = rows.some((r) => r.key === save.key)
	return found
		? rows.map((r) => (r.key === save.key ? { ...r, row: save.row, status: edited(r.status) } : r))
		: [...rows, created()]
}

/** Shows a saved row in its tab at once, before the server answers. */
function patchSaved(qc: QueryClient, kind: WorkfrontMappingKind, save: DraftRowSave) {
	if (kind === WorkfrontMappingKind.FIELD) {
		qc.setQueryData<FieldMappingListRow[]>(mappingKeys.fields, (rows) =>
			rows
				? upsert(rows, save, () => ({
						key: save.key,
						status: 'added',
						row: save.row as FieldMappingListRow['row'],
						sourceLabel: null,
						fallbackLabel: null,
						target: null,
					}))
				: rows,
		)
	} else if (kind === WorkfrontMappingKind.ASSET_TYPE) {
		qc.setQueryData<AssetTypeListRow[]>(mappingKeys.assetTypes, (rows) =>
			rows
				? upsert(rows, save, () => ({ key: save.key, status: 'added', row: save.row as AssetTypeListRow['row'] }))
				: rows,
		)
	} else if (kind === WorkfrontMappingKind.LOOKUP) {
		const row = save.row as LookupRow
		qc.setQueryData<LookupListRow[]>([...mappingKeys.lookups, row.lookup], (rows) =>
			rows ? upsert(rows, save, () => ({ key: save.key, status: 'added', row, sourceLabel: null })) : rows,
		)
	} else {
		const row = save.row as ValueRow
		qc.setQueriesData<ValueListRow[]>({ queryKey: mappingKeys.values }, (rows) =>
			rows?.map((r) =>
				r.key === save.key
					? {
							...r,
							valueId: row.valueId,
							valueLabel: save.valueLabel ?? r.valueLabel,
							geoValueId: row.geoValueId,
							match: WorkfrontValueMatch.MANUAL,
							status: edited(r.status),
						}
					: r,
			),
		)
	}
}

/** Puts the draft a save answered with in the status, so the next save is made on its revision. */
const keepDraft = (qc: QueryClient, draft: MappingStatusDTO['draft']) =>
	qc.setQueryData<MappingStatusDTO>(mappingKeys.status, (status) => (status ? { ...status, draft } : status))

/**
 * Save rows into the draft, one after another, each on the revision the last one
 * answered. Shown at once in their tab; any failure restores every tab, and the
 * server's state is reloaded either way. A refused stale revision is a 409.
 */
export const useSaveDraftRows = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async ({ kind, saves }: { kind: WorkfrontMappingKind; saves: DraftRowSave[] }) => {
			let draft = null
			for (const save of saves) {
				draft = await workfrontMappingsApi.saveDraftRow(kind, save.key, revisionOf(qc), save.row)
				keepDraft(qc, draft)
			}
			return draft
		},
		onMutate: async ({ kind, saves }) => {
			const snapshot = await snapshotOf(qc)
			for (const save of saves) patchSaved(qc, kind, save)
			return { snapshot }
		},
		onError: (_error, _vars, context) => restore(qc, context?.snapshot),
		onSettled: () => invalidateMappings(qc),
	})
}

/** Marks a row as the draft would leave it: deleted, or back to published when its change is reverted. */
function patchRemoved(qc: QueryClient, key: string, revert: boolean) {
	const apply = <T extends { key: string; status: MappingRowStatus | 'unmapped' }>(rows: T[] | undefined) =>
		rows
			?.filter((r) => !(revert && r.key === key && r.status === 'added'))
			.map((r) => (r.key === key ? { ...r, status: revert ? 'published' : 'deleted' } : r))
	for (const prefix of [mappingKeys.fields, mappingKeys.assetTypes, mappingKeys.lookups, mappingKeys.values]) {
		qc.setQueriesData<{ key: string; status: MappingRowStatus }[]>({ queryKey: prefix }, apply)
	}
}

/** Delete a published row in the draft, or revert the draft's change to it. Optimistic. */
export const useRemoveDraftRow = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ kind, key, revert }: { kind: WorkfrontMappingKind; key: string; revert: boolean }) =>
			workfrontMappingsApi.removeDraftRow(kind, key, revisionOf(qc), revert),
		onMutate: async ({ key, revert }) => {
			const snapshot = await snapshotOf(qc)
			patchRemoved(qc, key, revert)
			return { snapshot }
		},
		onSuccess: (draft) => keepDraft(qc, draft),
		onError: (_error, _vars, context) => restore(qc, context?.snapshot),
		onSettled: () => invalidateMappings(qc),
	})
}

/**
 * Discard the open draft, on the revision the caller saw: the draft bar goes at once.
 * The revision is passed in because `onMutate` clears the draft before the request.
 */
export const useDiscardDraft = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (revision: number) => workfrontMappingsApi.discardDraft(revision),
		onMutate: async () => {
			const snapshot = await snapshotOf(qc)
			keepDraft(qc, null)
			return { snapshot }
		},
		onError: (_error, _vars, context) => restore(qc, context?.snapshot),
		onSettled: () => invalidateMappings(qc),
	})
}

/** Leave an unmapped field out of the inventory, with a reason: it leaves the list at once. */
export const useIgnoreUnmappedField = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ parameterId, reason }: { parameterId: string; reason: string }) =>
			workfrontMappingsApi.ignoreUnmappedField(parameterId, reason),
		onMutate: async ({ parameterId }) => {
			const snapshot = await snapshotOf(qc)
			qc.setQueryData<UnmappedFieldDTO[]>(mappingKeys.unmapped, (fields) =>
				fields?.filter((field) => field.parameterId !== parameterId),
			)
			return { snapshot }
		},
		onError: (_error, _vars, context) => restore(qc, context?.snapshot),
		onSettled: () => invalidateMappings(qc),
	})
}

/** Map every stored bundle with the draft, without applying anything: a command with a result. */
export const usePreviewDraft = () => useMutation({ mutationFn: workfrontMappingsApi.preview })

const invalidateArea = (qc: QueryClient) => qc.invalidateQueries({ queryKey: WORKFRONT_KEY })

/** Publish the draft as a new version; the bundles re-map, so the whole area is refreshed. */
export const usePublishDraft = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (summary: string) => workfrontMappingsApi.publish(revisionOf(qc), summary),
		onSettled: () => invalidateArea(qc),
	})
}

/** Publish one row on its own, beside any open draft: an error's *Publish now*. */
export const useQuickPublish = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ kind, row, summary }: { kind: WorkfrontMappingKind; row: MappingRow; summary?: string }) =>
			workfrontMappingsApi.quickPublish(kind, row, summary),
		onSettled: () => invalidateArea(qc),
	})
}

/** Publish an older version's mappings as a new version: a roll back. */
export const useRepublishVersion = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ version, summary }: { version: number; summary?: string }) =>
			workfrontMappingsApi.republish(version, summary),
		onSettled: () => invalidateArea(qc),
	})
}

/** Ask for a bundle to be mapped again; it queues work, so it only invalidates. */
export const useRemapBundle = () => {
	const qc = useQueryClient()
	return useMutation({ mutationFn: workfrontMappingsApi.remapBundle, onSettled: () => invalidateArea(qc) })
}
